import {
	mergeClasses,
	Skeleton,
	SkeletonItem,
	Spinner,
	Tooltip,
	Tree,
	TreeItem,
	TreeItemLayout,
	TreeItemValue,
	TreeOpenChangeEvent,
	TreeProps,
} from '@fluentui/react-components';
import { ChevronDownRegular, ChevronRightRegular, ErrorCircleRegular } from '@fluentui/react-icons';
import { IFilterField } from '@infa-service/cdgc-connection';
import React, { FC, memo, useCallback, useState } from 'react';

import useStyle from './TreeStyle';

export const DQTreeSkeleton: React.FC = () => {
	const style = useStyle();
	return (
		<Skeleton animation="wave" appearance="translucent" className={style.skeletonContainer}>
			<SkeletonItem />
			<div className={style.skeletonRule}>
				<SkeletonItem size={16} shape="square" />
				<SkeletonItem />
			</div>
			<div className={style.skeletonDescription}>
				<SkeletonItem />
			</div>
			<div className={style.skeletonDescription}>
				<SkeletonItem />
			</div>
			<div className={style.skeletonRule}>
				<SkeletonItem size={16} shape="square" />
				<SkeletonItem />
			</div>
			<div className={style.skeletonDescription}>
				<SkeletonItem />
			</div>
			<div className={style.skeletonDescription}>
				<SkeletonItem />
			</div>
		</Skeleton>
	);
};

export type TreeItemType = {
	value: string;
	label: string;
	itemType: 'leaf' | 'branch' | 'root';
	element?: { type: string; name: string };
	filterValues?: IFilterField[];
	iconBefore?: React.JSX.Element;
	iconAfter?: React.JSX.Element;
	terminal?: boolean;
	moreElements?: boolean;
	parent?: string;
	offset?: number;
	pseudoExternalId?: string;
	isChildrenLoaded?: boolean;
	childrens: TreeItemType[] | [];
};

export type DQTreeProps = {
	props: Omit<TreeProps, 'onOpenChange' | 'openItems'>;
	treeItems: TreeItemType;
	onOpenChange: (value: TreeItemType, parentDetails: TreeItemType) => void;
	loadChildrens: (value: TreeItemType) => Promise<{ childrens: TreeItemType[]; moreElements: boolean } | undefined>;
	loadMoreNodes: (value: TreeItemType) => Promise<{ childrens: TreeItemType[]; moreElements: boolean } | undefined>;
	defaultOpenItems: TreeItemValue[];
	selectionEnabled?: boolean;
	defaultSelectedItems?: TreeItemValue;
};
enum TREE_LOADING_STATUS {
	LOADING,
	SUCCESS,
	FAILED,
	SHOW_MORE,
	SHOW_MORE_FAILED,
}
type TSubTree = {
	selectionEnabled?: boolean;
	subTreeData: TreeItemType;
	selectedLeafItem?: TreeItemType;
	handleLeafSelection: (value: TreeItemType, parentDetails: TreeItemType) => void;
	loadChildrens: (value: TreeItemType) => Promise<{ childrens: TreeItemType[]; moreElements: boolean } | undefined>;
};

const SubTree: FC<TSubTree> = (props) => {
	const style = useStyle();
	const { subTreeData, handleLeafSelection, loadChildrens, selectionEnabled, selectedLeafItem } = props;
	const [data, setData] = useState<TreeItemType>(subTreeData);
	const [loadingState, setLoadingState] = useState<TREE_LOADING_STATUS>();
	const [open, setOpen] = useState<boolean>(false);

	const addDynamicChildrens = async (selectedElement: TreeItemType) => {
		const response = await loadChildrens(selectedElement);
		if (!response) {
			throw new Error('browse failed');
		}
		const { childrens, moreElements } = response;
		const updatedChildrens = data?.childrens ? [...data.childrens, ...childrens] : [...childrens];
		setData((prev) => ({
			...prev,
			moreElements,
			isChildrenLoaded: true,
			offset: updatedChildrens.length,
			childrens: [...updatedChildrens],
		}));
	};

	const handleElementSelection = async (ev: TreeOpenChangeEvent, selectedElement: TreeItemType, parentDetails: TreeItemType) => {
		//? call the passed callback
		ev.stopPropagation();
		handleLeafSelection(selectedElement, parentDetails);
	};
	//! handling this seperatly in case we want to show skelton on show more
	const handleShomMore = useCallback(
		async (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
			try {
				ev.stopPropagation();
				setLoadingState(TREE_LOADING_STATUS.SHOW_MORE);
				await addDynamicChildrens(data);
				setLoadingState(TREE_LOADING_STATUS.SUCCESS);
			} catch (err) {
				setLoadingState(TREE_LOADING_STATUS.SHOW_MORE_FAILED);
			}
		},
		[addDynamicChildrens, data.childrens],
	);

	const handleOpenItem = useCallback(
		async (ev) => {
			try {
				ev.stopPropagation();
				if (data.isChildrenLoaded) {
					//? once childrens are loaded we don't need to call it again, we would have show more provision then
					setOpen(!open);
					return;
				}
				setLoadingState(TREE_LOADING_STATUS.LOADING);
				await addDynamicChildrens(data);
				setOpen(!open);
				setLoadingState(TREE_LOADING_STATUS.SUCCESS);
			} catch (err) {
				setLoadingState(TREE_LOADING_STATUS.FAILED);
			}
		},
		[data.isChildrenLoaded, open],
	);

	return (
		<TreeItem itemType="branch" onOpenChange={(ev) => handleOpenItem(ev)} open={open}>
			<TreeItemLayout
				expandIcon={
					loadingState === TREE_LOADING_STATUS.LOADING ? (
						<Spinner size="tiny" />
					) : loadingState === TREE_LOADING_STATUS.FAILED ? (
						<ErrorCircleRegular className={style.showMoreError} />
					) : open ? (
						<ChevronDownRegular />
					) : (
						<ChevronRightRegular />
					)
				}
				className={style.textOverflowHidden}
				iconBefore={subTreeData.iconBefore}
				iconAfter={subTreeData.iconAfter}
			>
				{subTreeData.label}
			</TreeItemLayout>
			<Tree>
				{data.childrens?.map((children) => {
					if (children.terminal) {
						return (
							<TreeItem
								className={mergeClasses(
									selectedLeafItem?.pseudoExternalId === children.pseudoExternalId && selectionEnabled && style.selectedItem,
									style.hover,
								)}
								key={children.pseudoExternalId}
								itemType="leaf"
								value={children.pseudoExternalId}
								onClick={(ev: TreeOpenChangeEvent) => handleElementSelection(ev, children, data)}
							>
								<Tooltip content={children.label} relationship="label">
									<TreeItemLayout className={mergeClasses(style.textOverflowHidden)} iconBefore={children.iconBefore} iconAfter={children.iconAfter}>
										{children.label}
									</TreeItemLayout>
								</Tooltip>
							</TreeItem>
						);
					} else {
						return <SubTree key={children.pseudoExternalId} {...props} subTreeData={children} />;
					}
				})}
				{data.moreElements ? (
					<>
						<TreeItem itemType="leaf" onClick={(ev) => handleShomMore(ev)}>
							{loadingState === TREE_LOADING_STATUS.SHOW_MORE ? (
								<></>
							) : loadingState === TREE_LOADING_STATUS.SHOW_MORE_FAILED ? (
								<TreeItemLayout iconBefore={<ErrorCircleRegular className={style.showMoreError} />} className={style.showMoreError}>
									Retry
								</TreeItemLayout>
							) : (
								<TreeItemLayout className={style.showMore}>Show More</TreeItemLayout>
							)}
						</TreeItem>
						{loadingState === TREE_LOADING_STATUS.SHOW_MORE ? (
							<Tree>
								{new Array(5).fill(1).map((_, index) => (
									<SkeletonItem key={index} className={style.skeletonDescription} />
								))}
							</Tree>
						) : null}
					</>
				) : null}
			</Tree>
		</TreeItem>
	);
};
const DQTree: FC<DQTreeProps> = ({ props, treeItems, defaultOpenItems, selectionEnabled = true, onOpenChange, loadChildrens, loadMoreNodes }) => {
	const style = useStyle();
	const [openItems, setOpenItems] = useState<Set<TreeItemValue>>(() => new Set(defaultOpenItems));
	const [selectedLeafItems, setSelectedLeafItems] = useState<TreeItemType>();
	const [loadingState, setLoadingState] = useState<TREE_LOADING_STATUS>();
	const [data, setData] = useState<TreeItemType>(treeItems);

	const handleOpenChange = (data: TreeItemType, parentDetails: TreeItemType) => {
		if (data.terminal) {
			setSelectedLeafItems(data);
		}
		!data.terminal ? setOpenItems((prev) => new Set(prev.add(data.value))) : null;
		onOpenChange(data, parentDetails);
	};

	const addMoreNodes = useCallback(async () => {
		const response = await loadMoreNodes(data);
		if (!response) {
			throw new Error('browse failed');
		}
		const { childrens: nodes, moreElements } = response;
		const updatedNodes = data?.childrens ? [...data.childrens, ...nodes] : [...nodes];
		setData((prev) => ({ ...prev, moreElements, offset: updatedNodes.length, childrens: [...updatedNodes] }));
	}, [data.childrens]);

	const handleShomMore = useCallback(
		async (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
			try {
				ev.stopPropagation();
				setLoadingState(TREE_LOADING_STATUS.SHOW_MORE);
				await addMoreNodes();
				setLoadingState(TREE_LOADING_STATUS.SUCCESS);
			} catch (err) {
				setLoadingState(TREE_LOADING_STATUS.SHOW_MORE_FAILED);
			}
		},
		[addMoreNodes],
	);
	const renderTreeItems = (treeItems: TreeItemType[], isArray?: boolean) => {
		let treeData = treeItems;
		if (!isArray) {
			treeData = treeItems[0].childrens;
		}
		const subtree =
			treeData?.length > 0 &&
			treeData.map((el) => {
				if (!el.terminal) {
					return (
						<>
							<SubTree
								loadChildrens={loadChildrens}
								subTreeData={el}
								selectionEnabled={selectionEnabled}
								handleLeafSelection={handleOpenChange}
								selectedLeafItem={selectedLeafItems}
							/>
						</>
					);
				}
				return (
					<TreeItem
						className={mergeClasses(selectedLeafItems?.pseudoExternalId === el.pseudoExternalId && selectionEnabled && style.selectedItem, style.hover)}
						key={el.pseudoExternalId}
						itemType="leaf"
						value={el.pseudoExternalId}
						onClick={() => handleOpenChange(el, treeData[0])}
					>
						<Tooltip content={el.label} relationship="label">
							<TreeItemLayout className={mergeClasses(style.textOverflowHidden)} iconBefore={el.iconBefore} iconAfter={el.iconAfter}>
								{el.label}
							</TreeItemLayout>
						</Tooltip>
					</TreeItem>
				);
			});
		return subtree;
	};

	return (
		<Tree aria-label="Open Item Controlled" {...props} openItems={openItems}>
			{data && Array.isArray(data) && renderTreeItems(data, true)}
			{data && !Array.isArray(data) && renderTreeItems([data])}
			{data.moreElements ? (
				<>
					<TreeItem itemType="leaf" onClick={(ev) => handleShomMore(ev)}>
						{loadingState === TREE_LOADING_STATUS.SHOW_MORE ? (
							<TreeItemLayout>
								<DQTreeSkeleton />
							</TreeItemLayout>
						) : loadingState === TREE_LOADING_STATUS.SHOW_MORE_FAILED ? (
							<TreeItemLayout iconBefore={<ErrorCircleRegular className={style.showMoreError} />} className={style.showMoreError}>
								Retry
							</TreeItemLayout>
						) : (
							<TreeItemLayout className={style.showMore}>Show More</TreeItemLayout>
						)}
					</TreeItem>
				</>
			) : null}
		</Tree>
	);
};

export default memo(DQTree);
